import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import moment from "moment";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AnalysisFilterField } from "../../../api/analysisApi";
import { FiscalYearFilterField } from "./FiscalYearFilterField";
import { MultiSelectFilterField } from "./MultiSelectFilterField";

// A real response's own options are usually bare strings ("Nissan", "United Kingdom", ...), not
// {label, value} objects - reading option.value/option.label off a string is undefined for both,
// which is exactly why every option rendered blank (no visible text, no value) despite the
// backend sending a full list. CPO Operational's own `year` field is the one seen so far that
// comes back as bare *numbers* instead (its backend schema is a real `List[int]`, not
// `List[str]`) - same undefined-value/blank-option failure mode, just for `typeof "number"`
// instead of a raw string. Normalizing here keeps the render below working for any primitive
// option (string, number, or already-a-{label,value}-object), rather than special-casing string
// alone and leaving every other primitive type to fall through unrendered. Exported so
// AnalyticsPage.tsx's own buildRequestFilters can resolve "All" into the same real option values
// this component renders, rather than a second, possibly-drifting copy of this same logic.
export const normalizeOption = (option: string | number | { label: string; value: string }): { label: string; value: string } =>
  typeof option === "object" ? option : { label: String(option), value: String(option) };

// `options` isn't a fixed schema (analysisApi.ts's own AnalysisFilterField comment) - a real
// dashboard has been seen returning a non-array value for it, which crashed `.map()` outright
// (`filter.options ?? []` only substitutes on null/undefined, not on some other truthy
// non-array value like an object). Treats anything that isn't actually an array as no options at
// all, same graceful-degradation reasoning as a date-type field having no options key.
export const toOptionsArray = (options: unknown): Array<string | number | { label: string; value: string }> =>
  Array.isArray(options) ? options : [];

// Every brand_performance page's own /analysis/.../filters response carries a real `fiscal_year`
// field whenever its underlying BigQuery service can compute one (BrandPerformanceBaseService's
// shared get_filters(), backend) - which in practice is true for all but two pages
// (brand_kpi_summary hides its date fields outright; nim_latest's own service never computes
// one). But the REAL Brand Performance dashboard doesn't turn that into a UI control for every
// page that has it - BrandPerformanceFilters.tsx's own hardcoded activePage list renders the
// Fiscal Year picker only for the pages below; brand_volume/sov_som/brand_market_share/
// segment_volume_and_segment_tiv_share_volume/tp/tpva get a plain preset date_range control
// instead (PurchaseFunnelDateFilter there), even though their own response also has a usable
// fiscal_year field. Mirrored here (not derived from "does the field exist") so a fiscal_year
// field is dropped back to a plain date_range field for every page NOT in this list, matching the
// real dashboard's own per-page choice rather than whatever the backend happened to compute.
export const BRAND_PERFORMANCE_FISCAL_YEAR_PAGES = new Set([
  "brand_oao",
  "brand_image",
  "brand_health_index",
  "brand_image_snapshot",
  "brand_kpi_trend",
  "buzz_tracking",
  "market_share_by_channel",
  "nim",
  "purchase_funnel",
  "residual_value",
  "table_for_download_monthly",
  "table_for_download_quarterly",
]);

// Which controls a date_range of "custom" brings up, decided per page here rather than read off the
// /filters response - those fields shift with whether the response's own options lookup succeeded
// (a "custom" range without its dates fails it), so one page can come back with fiscal_year once
// and start_date/end_date the next. Brand Performance's Image page shows its date_range dropdown
// with the Fiscal Year picker for "custom"; its preset pages (brand_volume, sov_som, ...) a
// start/end pair. Undefined leaves a page as its response has it.
export type CustomRangeControl = "fiscal_year" | "dates";
export const customRangeControl = (dashboardId: string, page?: string): CustomRangeControl | undefined => {
  if (dashboardId !== "brand_performance" || !page) return undefined;
  if (page === "brand_image") return "fiscal_year";
  return BRAND_PERFORMANCE_FISCAL_YEAR_PAGES.has(page) ? undefined : "dates";
};
export const usesDateRangeWithFiscalYear = (dashboardId: string, page?: string): boolean =>
  customRangeControl(dashboardId, page) === "fiscal_year";

// Rendered for a "dates" page's "custom" range even when its /filters response left them out (the
// backend hides them whenever it also returns fiscal_year), placed right after date_range.
const DATE_BOUND_FIELDS: AnalysisFilterField[] = [
  { key: "start_date", label: "Start Date", type: "date", default: null },
  { key: "end_date", label: "End Date", type: "date", default: null },
];
const withDateBoundFields = (filters: AnalysisFilterField[]): AnalysisFilterField[] => {
  const missing = DATE_BOUND_FIELDS.filter((field) => !filters.some((filter) => filter.key === field.key));
  if (missing.length === 0) return filters;
  const at = filters.findIndex((filter) => filter.key === "date_range") + 1;
  return [...filters.slice(0, at), ...missing, ...filters.slice(at)];
};

// Filter values travel as plain strings through onChange/values (matching every other field's own
// string values) - dates use the backend's own YYYY-MM-DD format rather than a locale-dependent
// one, same as PurchaseFunnelDateFilter.tsx's existing react-datepicker usage elsewhere.
const DATE_FORMAT = "YYYY-MM-DD";

const parseDateValue = (raw: unknown): Date | null => {
  if (typeof raw !== "string" || !raw) return null;
  const parsed = moment(raw, DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

interface Props {
  dashboardId: string;
  dashboardName: string;
  filters: AnalysisFilterField[];
  values: Record<string, unknown>;
  hasAppliedFilters: boolean;
  // True while this dashboard's /filters request (including its own internal retries) is still in
  // flight - filters is [] during this window for the same reason it's [] for a genuinely
  // filterless dashboard, so this is what tells the two states apart below.
  isLoadingFilters: boolean;
  // True once this dashboard's /filters request has come back 403 - filters is [] here too (the
  // request never got as far as returning any), but retrying won't help until access is actually
  // granted, unlike isLoadingFilters/a genuinely filterless dashboard.
  filtersAccessDenied: boolean;
  // Only set once AnalyticsPage.tsx has discovered a dashboard's real export names (see its own
  // findExportOptions) - undefined until then, and undefined forever for a dashboard with only
  // one option, since the backend doesn't need (or want) an `export` param in that case.
  exportOptions?: Array<{ value: string; label: string }>;
  exportName?: string;
  // True once AnalyticsPage.tsx has discovered (from a real error message - see its own
  // isPageRequiredError) that this dashboard is registered with more than one named "page" and
  // needs an explicit one - currently only brand_performance (brand_volume/sov_som/purchase_funnel),
  // but nothing here assumes that specific dashboard; any dashboard the backend flags this way
  // gets the same page input.
  needsPage: boolean;
  page: string;
  // The real page names for a dashboard registered with more than one (e.g. brand_performance's
  // brand_volume/sov_som/purchase_funnel/...), already resolved server-side - undefined for every
  // single-page dashboard. Lets the user choose which page to export the CSV from proactively,
  // rather than needsPage's own free-text fallback (kept only for the rare response that doesn't
  // carry this).
  pageOptions?: Array<{ value: string; label: string }>;
  onPageChange: (page: string) => void;
  onExportNameChange: (name: string) => void;
  onChange: (key: string, value: string) => void;
  onApply: () => void;
  onReset: () => void;
  onRemove: () => void;
  onDownloadCsv: () => void;
  // True from the moment this dashboard's Download CSV is clicked until its request settles
  // (success or failure) - the only feedback the button previously gave a click was... none, the
  // button just sat there through however long the export took. Failure still surfaces via the
  // existing toast.error in AnalyticsPage.tsx; this only covers the "something is happening" gap.
  isDownloadingCsv: boolean;
  // Why the last Apply Filters click was refused (e.g. "custom" with no start/end date) - shown in
  // place of the usual helper text, with Download CSV left hidden since nothing was applied.
  applyError?: string;
}

// The backend's /analysis/{dashboard_id}/filters endpoint only ever returns the dynamic filter
// schema (dropdown options, re-derived from whatever's currently selected) - there's no "preview
// results" endpoint, so unlike the client-side mock this tile used to render, Apply Filters here
// just confirms the current selections are valid and refreshes each field's own options; the
// actual data only ever comes back as a real file from Download CSV (/analysis/{id}/export).
export const ApplyFiltersTile = (props: Props) => {
  const {
    dashboardId,
    dashboardName,
    filters,
    values,
    hasAppliedFilters,
    isLoadingFilters,
    filtersAccessDenied,
    exportOptions,
    exportName,
    needsPage,
    page,
    pageOptions,
    onPageChange,
    onExportNameChange,
    onChange,
    onApply,
    onReset,
    onRemove,
    onDownloadCsv,
    isDownloadingCsv,
    applyError,
  } = props;

  // Whether the date pickers below offer day-level precision or are restricted to month+year -
  // driven by this same response's own `granularity` field (if present), not a fixed assumption,
  // since not every dashboard's data can be broken down to a single day. Only matches a bare
  // "daily" string/option (confirmed as the real shape), not any richer object some other
  // dashboard's granularity field might use.
  const granularityField = filters.find((filter) => filter.key === "granularity");
  const hasDailyGranularity = toOptionsArray(granularityField?.options)
    .map(normalizeOption)
    .some((option) => option.value === "daily");

  const startDateValue = parseDateValue(values.start_date ?? filters.find((f) => f.key === "start_date")?.default);
  const endDateValue = parseDateValue(values.end_date ?? filters.find((f) => f.key === "end_date")?.default);

  // start_date/end_date are only meaningful once date_range is actually "custom" - every other
  // date_range option (last_quarter, this_month, ...) already implies its own start/end, so
  // showing these two otherwise would just be a pair of controls with nothing for them to do.
  const dateRangeValue = values.date_range ?? filters.find((f) => f.key === "date_range")?.default;
  const showDateFields = dateRangeValue === "custom";
  // The real Brand Performance dashboard never shows its own plain Date Range dropdown alongside
  // the Fiscal Year picker - each page uses exactly one or the other (BrandPerformanceFilters.tsx
  // renders either the fiscal-year MultiSelectFilter tree or PurchaseFunnelDateFilter's plain
  // date_range control, never both). The backend's own filter schema for a fiscal-year page still
  // includes a real `date_range` field descriptor alongside the synthetic `fiscal_year` one, so
  // without this the generic renderer would show both, each capable of overwriting the same
  // `values.date_range` the other just set. FiscalYearFilterField fully owns date_range's
  // submission whenever it's present - except on a page where usesDateRangeWithFiscalYear holds:
  // there the dropdown always shows, and the picker only while it's on "custom", writing to its
  // own `fiscal_year` key so the dropdown keeps showing "custom" (AnalyticsPage.tsx's
  // buildRequestFilters folds the picked periods back into date_range on submission).
  const hasFiscalYearField = filters.some((filter) => filter.type === "fiscal_year");
  const dateRangeWithFiscalYear = usesDateRangeWithFiscalYear(dashboardId, page);
  const hideDateRangeField = hasFiscalYearField && !dateRangeWithFiscalYear;
  const showFiscalYearField = !dateRangeWithFiscalYear || dateRangeValue === "custom";
  // Every real dashboard's own filter layout (e.g. BrandPerformanceFilters.tsx) renders its
  // date/fiscal-year control first, before brand/market/anything else - but the backend's own
  // field order isn't guaranteed to put date_range/fiscal_year first (it isn't, for several
  // dashboards), so this tile sorted them otherwise. Array.prototype.sort is stable, so this only
  // ever promotes the one date/fiscal-year field - every other field keeps its existing relative
  // order untouched.
  const isDateOrFiscalYearField = (filter: AnalysisFilterField) => filter.type === "fiscal_year" || filter.key === "date_range";
  const renderedFilters = customRangeControl(dashboardId, page) === "dates" ? withDateBoundFields(filters) : filters;
  const visibleFilters = renderedFilters
    .filter(
      (filter) =>
        (filter.type !== "date" || showDateFields) &&
        !(filter.key === "date_range" && hideDateRangeField) &&
        (filter.type !== "fiscal_year" || showFiscalYearField)
    )
    .sort((a, b) => Number(isDateOrFiscalYearField(b)) - Number(isDateOrFiscalYearField(a)));

  return (
    <div className="analysis_tile" data-test-id={`analysis-tile-apply-filters-${dashboardId}`}>
      <div className="analysis_tile_dashboard_header">
        <div>
          <span className="analysis_tile_label">DASHBOARD</span>
          <h2 className="analysis_tile_dashboard_name">{dashboardName}</h2>
        </div>
        <button
          className="analysis_tile_close_button"
          onClick={onRemove}
          aria-label={`Remove ${dashboardName}`}
          data-test-id={`analysis-remove-dashboard-${dashboardId}`}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="analysis_tile_divider" />

      <h3 className="analysis_tile_title">FILTERS</h3>

      {/* A dashboard needing a page value also has an empty `filters` (the backend 400s before
          computing any), but that's a "haven't tried the right page yet" state, not a genuine
          "nothing to configure" one - the page picker below covers it instead of this message. */}
      {pageOptions && pageOptions.length > 0 ? (
        <label className="analysis_tile_filter" data-test-id={`analysis-page-select-${dashboardId}`}>
          <span className="analysis_tile_filter_label">Page</span>
          <select
            value={page || pageOptions[0].value}
            onChange={(e) => onPageChange(e.target.value)}
            data-test-id={`analysis-page-${dashboardId}`}
          >
            {pageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : (
        needsPage && (
          <label className="analysis_tile_filter" data-test-id={`analysis-page-input-${dashboardId}`}>
            <span className="analysis_tile_filter_label">Page</span>
            <input
              type="text"
              value={page}
              placeholder="e.g. brand_volume"
              onChange={(e) => onPageChange(e.target.value)}
              data-test-id={`analysis-page-${dashboardId}`}
            />
          </label>
        )
      )}

      {/* filters.length (not visibleFilters.length) - a dashboard whose only fields are
          start_date/end_date temporarily has an empty visibleFilters while date_range isn't
          "custom" yet, which isn't the same thing as this dashboard genuinely having no
          filterable fields at all. Without this, a truly filterless dashboard rendered the
          "FILTERS" heading directly above an empty grid with nothing under it - reading as broken/
          unfinished rather than a deliberate "there's nothing to configure here" state.

          filters is also [] while the very first /filters request for this dashboard is still in
          flight, or while it's come back 403 - isLoadingFilters/filtersAccessDenied are what tell
          those two states apart from a genuinely filterless dashboard, so a loading dashboard gets
          a spinner and a denied one gets an accurate message, instead of both momentarily/
          permanently flashing "no configurable filters" (which reads as a bug, not a permissions
          issue, and doesn't tell the user what would actually fix it). */}
      {filters.length === 0 &&
        !needsPage &&
        (isLoadingFilters ? (
          <div className="analysis_tile_loading" data-test-id={`analysis-filters-loading-${dashboardId}`}>
            <CircularProgress size={18} />
            <span>Loading filters…</span>
          </div>
        ) : filtersAccessDenied ? (
          <p className="analysis_tile_helper_text" data-test-id={`analysis-filters-access-denied-${dashboardId}`}>
            You don&apos;t have access to this dashboard&apos;s data - contact the CEDAR team if you believe this is an error.
          </p>
        ) : (
          <p className="analysis_tile_helper_text" data-test-id={`analysis-no-filters-${dashboardId}`}>
            This dashboard has no configurable filters - Download CSV works directly against its full data.
          </p>
        ))}

      <div className="analysis_tile_filters">
        {visibleFilters.map((filter) => {
          // Every other field type is a single native form control, so wrapping it in a <label>
          // gives free label/control association. The multiselect checkbox-dropdown has several
          // (trigger button, backdrop, checkboxes, Done button) - nesting all of that inside one
          // unlabeled <label> makes the browser forward a click on any non-control descendant
          // (the backdrop) to the label's first labelable descendant (the trigger button),
          // reopening the popover the instant the backdrop's own handler closes it. A plain <div>
          // keeps the identical layout/className without that implicit forwarding behavior.
          const usesCheckboxDropdown = filter.type === "multiselect" && toOptionsArray(filter.options).length > 0;
          const FilterRow = usesCheckboxDropdown ? "div" : "label";
          return (
            <FilterRow key={filter.key} className="analysis_tile_filter">
              <span className="analysis_tile_filter_label">{filter.label ?? filter.key}</span>
              {(() => {
                // A synthetic field (services/analysis/service.py's own describe_filters
                // handling) - its `options` are a nested {fiscal_year, quarters | months} array,
                // never the flat string/{label,value} list every other field's own `options`
                // already is, so it needs its own render path rather than the generic <select>
                // below (which - before this branch existed - read every entry's own .label/.value
                // as undefined, rendering a set of blank options that looked like nothing had
                // loaded at all).
                if (filter.type === "fiscal_year") {
                  return (
                    <FiscalYearFilterField
                      options={Array.isArray(filter.options) ? (filter.options as any) : []}
                      onChange={onChange}
                      testId={`analysis-filter-${dashboardId}-${filter.key}`}
                      {...(dateRangeWithFiscalYear ? { value: String(values.fiscal_year ?? ""), valueKey: "fiscal_year" } : {})}
                    />
                  );
                }

                if (filter.type === "date") {
                  return (
                    <div className="analysis_tile_date_picker" data-test-id={`analysis-filter-${dashboardId}-${filter.key}`}>
                      <DatePicker
                        selected={filter.key === "end_date" ? endDateValue : startDateValue}
                        onChange={(date: Date | null) => onChange(filter.key, date ? moment(date).format(DATE_FORMAT) : "")}
                        showMonthYearPicker={!hasDailyGranularity}
                        dateFormat={hasDailyGranularity ? "yyyy-MM-dd" : "MMMM yyyy"}
                        placeholderText={hasDailyGranularity ? "Select a date" : "Select month"}
                        selectsStart={filter.key === "start_date"}
                        selectsEnd={filter.key === "end_date"}
                        startDate={startDateValue}
                        endDate={endDateValue}
                        minDate={filter.key === "end_date" ? startDateValue ?? undefined : undefined}
                        isClearable
                      />
                    </div>
                  );
                }

                const fieldOptions = toOptionsArray(filter.options).map(normalizeOption);
                // A field can have a real default with no options array at all (e.g. Ecommerce's
                // `currency` - just {default: "USD"}, no list to pick from), or come back with none
                // because the backend's options lookup failed. Still a select, never free text: an
                // empty <select> has no <option> for the browser to match its value against and
                // renders blank, so the current value is offered as its only option (still sent
                // as-is), or a disabled placeholder when there's no value either.
                if (fieldOptions.length === 0) {
                  const currentValue = String(values[filter.key] ?? filter.default ?? "");
                  return (
                    <select
                      value={currentValue}
                      onChange={(e) => onChange(filter.key, e.target.value)}
                      data-test-id={`analysis-filter-${dashboardId}-${filter.key}`}
                    >
                      {currentValue ? (
                        <option value={currentValue}>{currentValue}</option>
                      ) : (
                        <option value="" disabled>
                          No options available
                        </option>
                      )}
                    </select>
                  );
                }

                // A multiselect field with a real options list gets the full checkbox-dropdown (lets
                // the user pick more than one, same as every "normal" dashboard's own market/region
                // filter) - "All" is that component's own top checkbox, not a plain <option> here.
                if (filter.type === "multiselect") {
                  return (
                    <MultiSelectFilterField
                      options={fieldOptions}
                      value={String(values[filter.key] ?? filter.default ?? "")}
                      onChange={(value) => onChange(filter.key, value)}
                      testId={`analysis-filter-${dashboardId}-${filter.key}`}
                    />
                  );
                }

                return (
                  <select
                    value={String(values[filter.key] ?? filter.default ?? "")}
                    onChange={(e) => onChange(filter.key, e.target.value)}
                    data-test-id={`analysis-filter-${dashboardId}-${filter.key}`}
                  >
                    {fieldOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                );
              })()}
            </FilterRow>
          );
        })}
      </div>

      {exportOptions && !filtersAccessDenied && (
        <label className="analysis_tile_filter" data-test-id={`analysis-export-select-${dashboardId}`}>
          <span className="analysis_tile_filter_label">Export</span>
          <select value={exportName ?? exportOptions[0].value} onChange={(e) => onExportNameChange(e.target.value)}>
            {exportOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* No Apply Filters/Reset/Download CSV at all for a dashboard the user can't access - there's
          nothing valid for any of them to do, and showing them (even disabled) invites clicking a
          button that can never work rather than making the actual problem (no access) obvious. */}
      {!filtersAccessDenied && (
        <>
          <div className="analysis_tile_actions">
            <button
              className="analysis_button_primary"
              onClick={onApply}
              // A dashboard with no configurable filters at all has nothing for this to do -
              // disabled rather than letting it fire a no-op request. AnalyticsPage.tsx marks the
              // dashboard as already-applied itself the moment this is known, so Download CSV
              // doesn't wait on a click that can no longer happen. Stays enabled while needsPage,
              // even though filters is also empty there - that's exactly the button the user needs
              // to click once they've typed a page value.
              disabled={filters.length === 0 && !needsPage}
              title={
                filters.length === 0 && !needsPage
                  ? isLoadingFilters
                    ? "Still loading this dashboard's filters"
                    : "This dashboard has no configurable filters"
                  : undefined
              }
              data-test-id={`analysis-apply-filters-${dashboardId}`}
            >
              Apply Filters
            </button>
            <button className="analysis_tile_reset_button" onClick={onReset} data-test-id={`analysis-reset-filters-${dashboardId}`}>
              Reset
            </button>
            {hasAppliedFilters && (
              <button
                className="analysis_tile_download_csv_button"
                onClick={onDownloadCsv}
                disabled={isDownloadingCsv}
                data-test-id={`analysis-download-csv-${dashboardId}`}
              >
                {isDownloadingCsv ? (
                  <CircularProgress size={16} color="inherit" data-test-id={`analysis-download-csv-spinner-${dashboardId}`} />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
                {isDownloadingCsv ? "Downloading…" : "Download CSV"}
              </button>
            )}
          </div>

          <div className="analysis_tile_divider" />
          {applyError ? (
            <p className="analysis_tile_error_text" role="alert" data-test-id={`analysis-apply-error-${dashboardId}`}>
              {applyError}
            </p>
          ) : (
            <p className="analysis_tile_helper_text">
              {hasAppliedFilters
                ? "Filters applied - ready to download."
                : needsPage
                ? "Enter a page above, then Apply Filters to continue."
                : "Apply filters before downloading."}
            </p>
          )}
        </>
      )}
    </div>
  );
};
